import React from 'react';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

/**
 * Polling status indicator component
 * Shows the current state of async polling operations
 */
const PollingIndicator = ({
  status, // 'idle' | 'polling' | 'complete' | 'error'
  elapsedTime = 0,
  pollCount = 0,
  message,
  className = ''
}) => {
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (status === 'idle') return null;

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {status === 'polling' && (
        <>
          <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-700">
              {message || 'Processing...'}
            </div>
            <div className="text-xs text-gray-500 flex items-center mt-0.5">
              <Clock className="w-3 h-3 mr-1" />
              {formatTime(elapsedTime)} • {pollCount} checks
            </div>
          </div>
        </>
      )}

      {status === 'complete' && (
        <>
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div className="text-sm font-medium text-green-700">
            {message || 'Complete!'}
          </div>
        </>
      )}

      {status === 'error' && (
        <>
          <XCircle className="w-5 h-5 text-red-500" />
          <div className="text-sm font-medium text-red-700">
            {message || 'Error occurred'}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Full-page polling overlay for major operations
 */
export const PollingOverlay = ({
  isVisible,
  status,
  title,
  message,
  elapsedTime,
  onCancel
}) => {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'polling' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {title || 'Processing Request'}
              </h3>
              <p className="text-gray-600 mb-4">
                {message || 'Amazon is processing your request. Please wait...'}
              </p>
              <div className="text-sm text-gray-500 flex items-center justify-center">
                <Clock className="w-4 h-4 mr-1" />
                Elapsed: {Math.floor(elapsedTime / 1000)}s
              </div>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Cancel
                </button>
              )}
            </>
          )}

          {status === 'complete' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {title || 'Success!'}
              </h3>
              <p className="text-gray-600">
                {message || 'Operation completed successfully.'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {title || 'Error'}
              </h3>
              <p className="text-gray-600">
                {message || 'An error occurred during processing.'}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PollingIndicator;
